import React, { useEffect, useMemo, useState } from "react";
import {
  IonButtons,
  IonCard,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonMenuToggle,
  IonPage,
  IonRow,
  IonSearchbar,
  IonTitle,
  IonToggle,
  IonToolbar,
} from "@ionic/react";
import { cube, eye, menu, tabletLandscape } from "ionicons/icons";
import { usePictures } from "../../Pictures/usePictures";
import { useCommon } from "../../../common/useCommon";
import { IPicture, PictureFlag } from "../../../models";
import { canHaveFrame } from "../../Cart/useCart";

const ON = "var(--ion-color-success-shade)";
const OFF = "var(--ion-color-danger-shade)";

const Flag = ({ country }: { country: "SRB" | "BIH" }) => (
  <img
    src={`assets/flags/${country}.svg`}
    alt={country === "SRB" ? "Srbija" : "BiH"}
    style={{ width: 24, height: 16, flexShrink: 0 }}
  />
);

/** One labelled switch: icon (green on / red off), short label, toggle. */
const FlagToggle = ({
  icon,
  label,
  ariaLabel,
  checked,
  onChange,
}: {
  icon: string;
  label: string;
  ariaLabel: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      minHeight: 34,
    }}
  >
    <IonIcon
      icon={icon}
      style={{ fontSize: 18, flexShrink: 0, color: checked ? ON : OFF }}
    />
    <span className="f13" style={{ flex: 1, minWidth: 0 }}>
      {label}
    </span>
    <IonToggle
      aria-label={ariaLabel}
      checked={checked}
      onIonChange={(e) => onChange(e.detail.checked)}
    />
  </div>
);

const CountryHeader = ({ country }: { country: "SRB" | "BIH" }) => (
  <div
    className="b f13"
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginTop: 6,
      paddingTop: 4,
      borderTop: "1px solid var(--ion-color-light-shade)",
    }}
  >
    <Flag country={country} />
    {country === "SRB" ? "Srbija" : "Bosna i Hercegovina"}
  </div>
);

const Legend = () => (
  <IonCard style={{ margin: "8px 5px" }}>
    <IonCardContent style={{ padding: 10 }}>
      <div className="b f15" style={{ marginBottom: 6 }}>
        Legenda
      </div>
      {[
        {
          icon: <Flag country="SRB" />,
          text: "Podešavanja za kupce iz Srbije (cene u RSD)",
        },
        {
          icon: <Flag country="BIH" />,
          text: "Podešavanja za kupce iz BiH (cene u KM)",
        },
        {
          icon: <IonIcon icon={eye} style={{ fontSize: 20 }} />,
          text: "Vidljiva – slika se prikazuje na sajtu. Isključeno: potpuno sakrivena.",
        },
        {
          icon: <IonIcon icon={cube} style={{ fontSize: 20 }} />,
          text: "Na stanju – može u korpu. Isključeno: kupac može samo da rezerviše.",
        },
        {
          icon: <IonIcon icon={tabletLandscape} style={{ fontSize: 20 }} />,
          text: "Sklopljen ram – kupac može da izabere platno montirano na sklopljen ram (samo Srbija).",
        },
      ].map((row, i) => (
        <div
          key={i}
          className="f13"
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}
        >
          <div style={{ width: 24, display: "flex", justifyContent: "center" }}>
            {row.icon}
          </div>
          <div style={{ flex: 1 }}>{row.text}</div>
        </div>
      ))}
      <div className="f13" style={{ marginTop: 6 }}>
        Boja ikonice: <span style={{ color: ON }}>zelena = uključeno</span>,{" "}
        <span style={{ color: OFF }}>crvena = isključeno</span>.
      </div>
    </IonCardContent>
  </IonCard>
);

const Availability = () => {
  useCommon();
  const { allPicturesAdmin, getAllPicturesAdmin, updatePictureFlag } =
    usePictures();
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    if (allPicturesAdmin.length === 0) getAllPicturesAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pictures = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allPicturesAdmin
      .filter(
        (p) =>
          !term ||
          p.code.toLowerCase().includes(term) ||
          (p.title || "").toLowerCase().includes(term)
      )
      .sort((a, b) => (a.code > b.code ? 1 : -1));
  }, [allPicturesAdmin, search]);

  const set = (pic: IPicture, flag: PictureFlag, value: boolean) =>
    updatePictureFlag(pic.id, flag, value);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="warning">
          <IonButtons slot="start">
            <IonMenuToggle>
              <IonIcon icon={menu} style={{ fontSize: 28 }} />
            </IonMenuToggle>
          </IonButtons>
          <IonTitle>Ažuriranje stanja</IonTitle>
        </IonToolbar>
        <IonToolbar color="warning">
          <IonSearchbar
            value={search}
            placeholder="Šifra ili naziv"
            debounce={200}
            onIonInput={(e) => setSearch(e.detail.value || "")}
          />
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Legend />
        <IonGrid style={{ padding: 2 }}>
          <IonRow>
            {pictures.map((pic) => (
              <IonCol key={pic.id} size="6" sizeSm="4" sizeMd="3">
                <IonCard style={{ margin: 0, height: "100%" }}>
                  <picture>
                    <source
                      media="(min-width:650px)"
                      srcSet={`assets/pics/${pic.code}.jpg`}
                    />
                    <img
                      src={`assets/pics/320/${pic.code}.jpg`}
                      alt={pic.title}
                      style={{
                        width: "100%",
                        opacity: pic.isDeleted && pic.isDeletedBIH ? 0.25 : 1,
                      }}
                    />
                  </picture>
                  <IonCardContent style={{ padding: "4px 6px 8px" }}>
                    <div className="b f15 ion-text-center">{pic.code}</div>
                    <div
                      className="f13 ion-text-center"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {pic.title}
                    </div>

                    <CountryHeader country="SRB" />
                    <FlagToggle
                      icon={eye}
                      label="Vidljiva"
                      ariaLabel={`${pic.code} – Srbija – vidljiva`}
                      checked={!pic.isDeleted}
                      onChange={(checked) => set(pic, "isDeleted", !checked)}
                    />
                    <FlagToggle
                      icon={cube}
                      label="Na stanju"
                      ariaLabel={`${pic.code} – Srbija – na stanju`}
                      checked={pic.status}
                      onChange={(checked) => set(pic, "status", checked)}
                    />
                    {canHaveFrame(pic) && (
                      <FlagToggle
                        icon={tabletLandscape}
                        label="Sklopljen ram"
                        ariaLabel={`${pic.code} – Srbija – sklopljen ram moguć`}
                        checked={pic.frameAssembled}
                        onChange={(checked) =>
                          set(pic, "frameAssembled", checked)
                        }
                      />
                    )}

                    <CountryHeader country="BIH" />
                    <FlagToggle
                      icon={eye}
                      label="Vidljiva"
                      ariaLabel={`${pic.code} – BiH – vidljiva`}
                      checked={!pic.isDeletedBIH}
                      onChange={(checked) => set(pic, "isDeletedBIH", !checked)}
                    />
                    <FlagToggle
                      icon={cube}
                      label="Na stanju"
                      ariaLabel={`${pic.code} – BiH – na stanju`}
                      checked={pic.statusBIH}
                      onChange={(checked) => set(pic, "statusBIH", checked)}
                    />
                  </IonCardContent>
                </IonCard>
              </IonCol>
            ))}
          </IonRow>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};

export default Availability;
